import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
type ViewType = 'consultant' | 'radiology' | 'manager';
export function HcpRoleViewSlide({
  onNext,
  onBack
}: {
  onNext: () => void;
  onBack: () => void;
}) {
  const [view, setView] = useState<ViewType>('consultant');
  const views = [{
    id: 'consultant',
    label: 'Consultant'
  }, {
    id: 'radiology',
    label: 'Radiology'
  }, {
    id: 'manager',
    label: 'Service manager'
  }];
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-slate-950 text-slate-300" data-id="element-232">
      <div className="flex items-center justify-between mb-6" data-id="element-233">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-white transition-colors bg-slate-900 rounded-full shadow-sm border border-slate-800" aria-label="Go back" data-id="element-234">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-235" />
        </button>
      </div>

      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full overflow-y-auto no-scrollbar pb-24" data-id="element-236">
        {/* Toggle */}
        <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-800 mb-8" data-id="element-237">
          {views.map(v => <button key={v.id} onClick={() => setView(v.id as ViewType)} className={`flex-1 py-2 px-2 text-[11px] sm:text-xs font-medium rounded-lg transition-all ${view === v.id ? 'bg-nhs-blue text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-238">
              {v.label}
            </button>)}
        </div>

        <AnimatePresence mode="wait" data-id="element-239">
          {view === 'consultant' && <motion.div key="consultant" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-6" data-id="element-240">
              <div data-id="element-241">
                <h2 className="text-2xl font-semibold text-white leading-tight mb-3" data-id="element-242">
                  Clinic allocation becomes faster
                </h2>
                <p className="text-slate-400 leading-relaxed" data-id="element-243">
                  Grouped patients can be reviewed and confirmed more quickly
                  than individual referral letters.
                </p>
              </div>

              {/* Workflow visual */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col items-center space-y-2" data-id="element-244">
                <div className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-245">
                  Grouped patients
                </div>
                <ArrowDownIcon className="w-4 h-4 text-slate-500" data-id="element-246" />
                <div className="bg-nhs-blue/20 border border-nhs-blue/30 text-nhs-lightblue px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-247">
                  Consultant confirmation
                </div>
                <ArrowDownIcon className="w-4 h-4 text-slate-500" data-id="element-248" />
                <div className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-249">
                  Clinic schedule
                </div>
              </div>

              {/* Comparison */}
              <div className="grid grid-cols-2 gap-3" data-id="element-250">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-251">
                  <span className="text-slate-400 text-xs mb-2" data-id="element-252">
                    Manual triage
                  </span>
                  <span className="text-white text-xl font-bold" data-id="element-253">4 hours</span>
                  <span className="text-slate-500 text-[10px] uppercase tracking-wider mt-1" data-id="element-254">
                    / week
                  </span>
                </div>
                <div className="bg-nhs-blue/10 border border-nhs-blue/20 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-255">
                  <span className="text-nhs-lightblue text-xs mb-2" data-id="element-256">
                    Structured triage
                  </span>
                  <span className="text-white text-xl font-bold" data-id="element-257">
                    20–30 min
                  </span>
                  <span className="text-slate-500 text-[10px] uppercase tracking-wider mt-1" data-id="element-258">
                    / week
                  </span>
                </div>
              </div>

              <p className="text-sm text-slate-400 text-center italic" data-id="element-259">
                The consultant focuses on decisions, not sorting.
              </p>
            </motion.div>}

          {view === 'radiology' && <motion.div key="radiology" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-6" data-id="element-260">
              <div data-id="element-261">
                <h2 className="text-2xl font-semibold text-white leading-tight mb-3" data-id="element-262">
                  Imaging needs become visible earlier
                </h2>
                <p className="text-slate-400 leading-relaxed" data-id="element-263">
                  Knowing symptom type and previous investigation status helps
                  radiology prepare imaging capacity in advance.
                </p>
              </div>

              <div className="space-y-3" data-id="element-264">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex justify-between items-center" data-id="element-265">
                  <span className="text-slate-300 font-medium" data-id="element-266">
                    Previous imaging available
                  </span>
                  <span className="text-nhs-lightblue font-bold bg-nhs-blue/10 px-3 py-1 rounded-lg" data-id="element-267">
                    9 patients
                  </span>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex justify-between items-center" data-id="element-268">
                  <span className="text-slate-300 font-medium" data-id="element-269">
                    First imaging required
                  </span>
                  <span className="text-amber-400 font-bold bg-amber-500/10 px-3 py-1 rounded-lg" data-id="element-270">
                    25 patients
                  </span>
                </div>
              </div>

              {/* Workflow visual */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col items-center space-y-2" data-id="element-271">
                <div className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-272">
                  Patient intake
                </div>
                <ArrowDownIcon className="w-4 h-4 text-slate-500" data-id="element-273" />
                <div className="bg-nhs-blue/20 border border-nhs-blue/30 text-nhs-lightblue px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-274">
                  Imaging preparation
                </div>
                <ArrowDownIcon className="w-4 h-4 text-slate-500" data-id="element-275" />
                <div className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium w-full text-center" data-id="element-276">
                  Clinic day
                </div>
              </div>

              <p className="text-sm text-slate-400 text-center italic" data-id="element-277">
                Radiology teams can better anticipate ultrasound and mammography
                needs.
              </p>
            </motion.div>}

          {view === 'manager' && <motion.div key="manager" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-6" data-id="element-278">
              <div data-id="element-279">
                <h2 className="text-2xl font-semibold text-white leading-tight mb-3" data-id="element-280">
                  Clinic demand becomes easier to manage
                </h2>
                <p className="text-slate-400 leading-relaxed" data-id="element-281">
                  Grouped patient data makes clinic demand visible before
                  scheduling decisions are made.
                </p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4" data-id="element-282">
                <div data-id="element-283">
                  <div className="flex justify-between text-sm mb-1" data-id="element-284">
                    <span className="text-white" data-id="element-285">Urgent clinic</span>
                    <span className="text-slate-400" data-id="element-286">14 patients</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden" data-id="element-287">
                    <div className="bg-rose-500 h-full rounded-full" style={{
                  width: '60%'
                }} data-id="element-288" />
                  </div>
                </div>

                <div data-id="element-289">
                  <div className="flex justify-between text-sm mb-1" data-id="element-290">
                    <span className="text-white" data-id="element-291">Routine clinic</span>
                    <span className="text-slate-400" data-id="element-292">16 patients</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden" data-id="element-293">
                    <div className="bg-amber-500 h-full rounded-full" style={{
                  width: '70%'
                }} data-id="element-294" />
                  </div>
                </div>

                <div data-id="element-295">
                  <div className="flex justify-between text-sm mb-1" data-id="element-296">
                    <span className="text-white" data-id="element-297">Pain clinic</span>
                    <span className="text-slate-400" data-id="element-298">8 patients</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden" data-id="element-299">
                    <div className="bg-sky-500 h-full rounded-full" style={{
                  width: '35%'
                }} data-id="element-300" />
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-400 text-center italic" data-id="element-301">
                This supports faster clinic allocation and better resource
                planning.
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="absolute bottom-6 left-6 right-6" data-id="element-302">
        <button onClick={onNext} className="w-full py-4 bg-nhs-blue text-white rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-nhs-darkblue transition-colors shadow-lg shadow-nhs-blue/20 active:scale-[0.98] group" data-id="element-303">
          See workflow impact
          <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-304" />
        </button>
      </motion.div>
    </div>;
}